// ─────────────────────────────────────────────────────────────────────────────
// Sale detail — mockup-styled view of a sale/discount with edit-in-place.
//
// Clicking a sale card lands here: hero card (hero-photo fallback chain,
// strength ring, meta chips), tinted psychology cards, the amber quote/promo
// strip echoing the card, the Applies To section (products / offers / whole
// brand resolved to thumbnailed tiles), then Terms & Conditions and Creative
// Notes. Every displayed field edits in place (click → input/select styled to
// match, partial PATCH on save); there is no separate edit form. Duplicate
// clones the sale via the create endpoint. Empty fields show a subtle "Add …"
// affordance so everything editable stays discoverable.
// ─────────────────────────────────────────────────────────────────────────────
import { useMemo } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Loader2, Trash2, Percent, Copy, Lightbulb, Timer,
  Megaphone, MessageSquareQuote, Brain, Target, Package, Tag, FileText,
  Paintbrush, Plus, X,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Offer, Product, SalesDiscount } from "@shared/schema";
import { optimizedImg } from "@/lib/img";
import {
  computeSaleStrength, isSaleStrengthReady, saleStrengthCaption,
  type SaleStrengthInput,
} from "@shared/sale-strength";
import {
  SALE_TYPES, URGENCY_MECHANISMS, APPLIES_TO_SCOPES, SALE_STATUSES,
} from "@shared/sales-discount-template";
import { InlineText, InlineTextarea, InlineSelect, InlineDate } from "@/components/inline-edit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Same serif stack as the sale/offering/persona cards' quote strips.
const SERIF = { fontFamily: `'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif` };

const SALE_TYPE_LABELS: Record<string, string> = Object.fromEntries(SALE_TYPES.map(t => [t.value, t.label]));
const URGENCY_LABELS: Record<string, string> = Object.fromEntries(URGENCY_MECHANISMS.map(u => [u.value, u.label]));

// ---------------------------------------------------------------------------
// Small display helpers
// ---------------------------------------------------------------------------
const hasText = (v: any): v is string => typeof v === "string" && v.trim().length > 0;
const fmtEnum = (v: any) => (hasText(v) ? v.replace(/_/g, " ") : "");

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("") || "?";

// "Ends Jun 21, 2026" (or null when no expiration is set / unparseable).
function expirationText(sale: SalesDiscount): string | null {
  if (!sale.expirationDate) return null;
  const d = new Date(sale.expirationDate as unknown as string);
  if (isNaN(d.getTime())) return null;
  return `Ends ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

// Urgency mechanism label — the 'no_urgency' default is not a mechanism.
function urgencyLabel(sale: SalesDiscount): string | null {
  const m = (sale.urgencyMechanism ?? "").trim();
  if (!m || m === "no_urgency") return null;
  return URGENCY_LABELS[m] || m.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Building blocks (mockup card language, matching offer-detail)
// ---------------------------------------------------------------------------

function StrengthRing({ pct }: { pct: number }) {
  const C = 2 * Math.PI * 26; // r=26 in a 64x64 viewBox
  return (
    <div className="relative w-[68px] h-[68px] flex-shrink-0" data-testid="ring-sale-strength">
      <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
        <circle cx="32" cy="32" r="26" fill="none" strokeWidth="5" className="stroke-muted/70" />
        <circle
          cx="32" cy="32" r="26" fill="none" strokeWidth="5" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * C} ${C}`}
          className={isSaleStrengthReady(pct) ? "stroke-emerald-500" : "stroke-emerald-600/70"}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums">
        {pct}%
      </span>
    </div>
  );
}

/** Meta chip whose value is an inline editor instead of static text. */
function EditableMetaChip({ label, children, testId }: { label: string; children: React.ReactNode; testId: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card px-3.5 py-2 min-w-[92px]" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70">{label}</p>
      <div className="text-sm font-semibold capitalize leading-snug">{children}</div>
    </div>
  );
}

/** Tinted icon square + uppercase section title, matching persona-detail. */
function SectionHeader({ icon: Icon, title, accent, count }: { icon: any; title: string; accent: string; count?: number }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <h2 className="text-xs font-bold uppercase tracking-[0.12em]">{title}</h2>
      {count !== undefined && count > 0 && (
        <span className="text-[10px] font-semibold rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{count}</span>
      )}
    </div>
  );
}

function SectionCard({ children, testId, className = "" }: { children: React.ReactNode; testId?: string; className?: string }) {
  return (
    <div className={`rounded-2xl border border-border/60 bg-card shadow-sm p-5 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

/** Small thumbnailed tile for a linked product/offer in Applies To. */
function LinkedEntityTile({ name, photo, kind, primary, onRemove, testId }: {
  name: string;
  photo: string | null;
  kind: "product" | "offer";
  primary?: boolean;
  onRemove?: () => void;
  testId: string;
}) {
  const Icon = kind === "product" ? Package : Tag;
  return (
    <div className="group/tile flex items-center gap-2.5 rounded-xl border border-border/60 bg-muted/20 pl-1.5 pr-3 py-1.5 min-w-0" data-testid={testId}>
      <div className="h-9 w-9 rounded-lg overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
        {photo ? (
          <img src={optimizedImg(photo, 160)} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span className="text-[10px] font-bold text-muted-foreground">{initials(name)}</span>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-snug truncate">{name}</p>
        <p className="text-[10px] text-muted-foreground/70 flex items-center gap-1">
          <Icon className="h-2.5 w-2.5" />
          <span className="capitalize">{kind}</span>
          {primary && <span className="rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 px-1.5 py-px font-semibold">Primary</span>}
        </p>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1 shrink-0 rounded-full p-0.5 text-muted-foreground/50 hover:text-destructive opacity-0 group-hover/tile:opacity-100 transition-opacity"
          title="Remove"
          data-testid={`button-remove-${testId}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function SalesDiscountDetail() {
  const { brandId, saleId } = useParams<{ brandId: string; saleId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const { data: sale, isLoading } = useQuery<SalesDiscount>({
    queryKey: ["/api/sales-discounts", saleId],
    queryFn: async () => {
      const res = await fetch(`/api/sales-discounts/${saleId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load sale");
      return res.json();
    },
    enabled: !!saleId,
    // The quote-strip sketch and the product-based AI hero image land in the
    // background (same lifecycle as the list page) — poll gently until both
    // are ready or terminally failed.
    refetchInterval: (query) => {
      const s = query.state.data as any;
      if (!s) return false;
      const generating = (url: unknown, status: unknown) => !url && status === "generating";
      const pending = (url: unknown, status: unknown) => !url && status !== "failed";
      if (generating(s.sketchImageUrl, s.sketchImageStatus) || generating(s.heroImageUrl, s.heroImageStatus)) return 4000;
      if (pending(s.sketchImageUrl, s.sketchImageStatus) || pending(s.heroImageUrl, s.heroImageStatus)) return 15000;
      return false;
    },
  });

  // Products and offers back the same hero-photo fallback chain the sale list
  // cards use (primary product photo → first appliesTo product photo →
  // primary offer's photo chain → placeholder) and name the Applies To tiles,
  // so the strength score and hero imagery agree between the card and here.
  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: offers } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`],
    enabled: !!brandId,
  });

  // Field-level partial PATCH shared by every inline editor. The server
  // returns the updated row, which replaces the detail cache directly so
  // rapid successive per-field saves never clobber each other with stale
  // refetches; the list query is invalidated the normal way.
  const saveMutation = useMutation({
    mutationFn: async (patch: Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/sales-discounts/${saleId}`, patch);
      return res.json();
    },
    onSuccess: (updated: SalesDiscount) => {
      queryClient.setQueryData(["/api/sales-discounts", saleId], updated);
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/sales-discounts`] });
    },
    onError: (e: Error) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });
  const save = (patch: Record<string, any>) => saveMutation.mutateAsync(patch);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/sales-discounts/${saleId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/sales-discounts`] });
      toast({ title: "Sale deleted" });
      navigate(`/brands/${brandId}/sales-discounts`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      if (!sale) throw new Error("Sale not loaded");
      // Every client-writable field is copied; the name gets a suffix and the
      // status resets to draft. (Sketch columns are server-managed — the copy
      // gets its own sketch from the background sweep.)
      const body = {
        name: `${sale.name} (Copy)`.slice(0, 500),
        status: "draft",
        saleType: sale.saleType,
        discountValue: sale.discountValue,
        appliesTo: sale.appliesTo,
        primaryProductId: sale.primaryProductId,
        primaryOfferId: sale.primaryOfferId,
        promotionSummary: sale.promotionSummary,
        customerFacingCopy: sale.customerFacingCopy,
        whyThisSaleExists: sale.whyThisSaleExists,
        urgencyMechanism: sale.urgencyMechanism,
        expirationDate: sale.expirationDate ? new Date(sale.expirationDate as any).toISOString() : null,
        promoCode: sale.promoCode,
        termsConditions: sale.termsConditions,
        ctaLanguage: sale.ctaLanguage,
        creativeNotes: sale.creativeNotes,
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/sales-discounts`, body);
      return res.json();
    },
    onSuccess: (created: SalesDiscount) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/sales-discounts`] });
      toast({ title: "Sale duplicated" });
      navigate(`/brands/${brandId}/sales-discounts/${created.id}`);
    },
    onError: (e: Error) => toast({ title: "Duplicate failed", description: e.message, variant: "destructive" }),
  });

  // ── Photo lookup maps (identical logic to the sales list page) ───────────
  const productPhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    products?.forEach(p => {
      const media = (p.mediaJson as unknown[] | null) || [];
      const refs = (p.adgenImageRefs as unknown[] | null) || [];
      const photo = [...media, ...refs].find((u): u is string => typeof u === "string" && !!u.trim());
      if (photo) map[p.id] = photo;
    });
    return map;
  }, [products]);

  // An offer's photo chain mirrors the offerings grid: its own reference
  // images first, then the first included product's photo.
  const offerPhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    offers?.forEach(o => {
      const refs = (o.adgenImageRefs as unknown[] | null) || [];
      const ownRef = refs.find((u): u is string => typeof u === "string" && !!u.trim());
      if (ownRef) {
        map[o.id] = ownRef;
        return;
      }
      const included = Array.isArray(o.includedProducts) ? (o.includedProducts as unknown[]) : [];
      for (const pid of included) {
        if (typeof pid === "string" && productPhotoById[pid]) {
          map[o.id] = productPhotoById[pid];
          return;
        }
      }
    });
    return map;
  }, [offers, productPhotoById]);

  if (isLoading) {
    return <div className="space-y-4 w-full px-8"><Skeleton className="h-10 w-48" /><Skeleton className="h-96" /></div>;
  }
  if (!sale) return <div className="p-6 text-muted-foreground">Sale not found</div>;

  // ── Derived view data ─────────────────────────────────────────────────────
  const appliesTo = (sale.appliesTo ?? null) as { scope?: string; productIds?: unknown; offerIds?: unknown } | null;
  const appliesProductIds = Array.isArray(appliesTo?.productIds)
    ? appliesTo!.productIds.filter((v): v is string => typeof v === "string" && !!v.trim())
    : [];
  const appliesOfferIds = Array.isArray(appliesTo?.offerIds)
    ? appliesTo!.offerIds.filter((v): v is string => typeof v === "string" && !!v.trim())
    : [];
  const scope = appliesTo?.scope || "brand";

  // Applies-to edits always rebuild the full jsonb object from the latest
  // cached row (concurrent per-field saves elsewhere can't clobber it).
  const saveAppliesTo = (next: { scope: string; productIds: string[]; offerIds: string[] }, extra: Record<string, any> = {}) =>
    save({ appliesTo: next, ...extra });
  const currentAppliesTo = () => ({ scope, productIds: [...appliesProductIds], offerIds: [...appliesOfferIds] });

  // Hero fallback chain — identical to the sale card: primary product photo →
  // first appliesTo product photo → primary offer's photo chain → placeholder.
  let heroImage: string | null = null;
  if (sale.primaryProductId && productPhotoById[sale.primaryProductId]) {
    heroImage = productPhotoById[sale.primaryProductId];
  } else {
    for (const pid of appliesProductIds) {
      if (productPhotoById[pid]) { heroImage = productPhotoById[pid]; break; }
    }
    if (!heroImage && sale.primaryOfferId && offerPhotoById[sale.primaryOfferId]) {
      heroImage = offerPhotoById[sale.primaryOfferId];
    }
  }

  // Image-panel chain: generated AI hero (product-based, lands in the
  // background) → real-photo chain → gradient placeholder. The strength
  // gauge keeps scoring the REAL-photo chain (same as the list card) so an
  // auto-generated hero doesn't inflate sale strength.
  const displayHero = (typeof (sale as any).heroImageUrl === "string" && (sale as any).heroImageUrl) || heroImage;

  // Same shared computation (and hero-photo input) as the sale card gauge —
  // the two surfaces can never disagree on score or readiness.
  const strength = computeSaleStrength({ ...(sale as SaleStrengthInput), hasHeroPhoto: !!heroImage });
  const ready = isSaleStrengthReady(strength);

  const typeLabel = sale.saleType ? (SALE_TYPE_LABELS[sale.saleType] || fmtEnum(sale.saleType)) : "";
  const urgency = urgencyLabel(sale);
  const expires = expirationText(sale);
  const quote = hasText(sale.customerFacingCopy) ? sale.customerFacingCopy!.trim() : "";
  const promoCode = hasText(sale.promoCode) ? sale.promoCode!.trim() : "";
  const sketch = hasText(sale.sketchImageUrl) ? sale.sketchImageUrl! : null;

  // Sale psychology tinted cards (persona PsychField style). The three
  // free-text cards edit in place; Urgency stays derived — its underlying
  // mechanism/expiration edit via the hero meta chips.
  const psychCards: {
    key: string; label: string; icon: any; tint: string; head: string;
    field?: "whyThisSaleExists" | "ctaLanguage" | "customerFacingCopy";
    text?: string | null; derivedHint?: string;
  }[] = [
    {
      key: "why-exists", label: "Why This Sale Exists", icon: Lightbulb, field: "whyThisSaleExists",
      tint: "bg-violet-50/70 border-violet-200/60 dark:bg-violet-950/25 dark:border-violet-900/40",
      head: "text-violet-600 dark:text-violet-300",
    },
    {
      key: "urgency", label: "Urgency", icon: Timer,
      text: urgency && expires ? `${urgency} — ${expires.toLowerCase()}` : (urgency || expires),
      derivedHint: "Set via the Urgency and Expiration chips above",
      tint: "bg-orange-50/70 border-orange-200/60 dark:bg-orange-950/25 dark:border-orange-900/40",
      head: "text-orange-600 dark:text-orange-300",
    },
    {
      key: "cta-language", label: "CTA Language", icon: Megaphone, field: "ctaLanguage",
      tint: "bg-emerald-50/70 border-emerald-200/60 dark:bg-emerald-950/25 dark:border-emerald-900/40",
      head: "text-emerald-600 dark:text-emerald-300",
    },
    {
      key: "customer-copy", label: "Customer-Facing Copy", icon: MessageSquareQuote, field: "customerFacingCopy",
      tint: "bg-amber-50/70 border-amber-200/60 dark:bg-amber-950/25 dark:border-amber-900/40",
      head: "text-amber-600 dark:text-amber-300",
    },
  ];

  // Applies To tiles — primary product/offer first, then the appliesTo lists.
  // Dangling ids (deleted products/offers) simply drop out.
  const productEntryIds = Array.from(new Set([
    ...(sale.primaryProductId ? [sale.primaryProductId] : []),
    ...appliesProductIds,
  ]));
  const offerEntryIds = Array.from(new Set([
    ...(sale.primaryOfferId ? [sale.primaryOfferId] : []),
    ...appliesOfferIds,
  ]));
  const productEntries = productEntryIds
    .map((id) => {
      const p = products?.find((x) => x.id === id);
      return p ? { id, name: p.name, photo: productPhotoById[id] || null, primary: id === sale.primaryProductId } : null;
    })
    .filter((e): e is NonNullable<typeof e> => !!e);
  const offerEntries = offerEntryIds
    .map((id) => {
      const o = offers?.find((x) => x.id === id);
      return o ? { id, name: o.name, photo: offerPhotoById[id] || null, primary: id === sale.primaryOfferId } : null;
    })
    .filter((e): e is NonNullable<typeof e> => !!e);

  const addableProducts = (products ?? []).filter((p) => !productEntryIds.includes(p.id));
  const addableOffers = (offers ?? []).filter((o) => !offerEntryIds.includes(o.id));

  const removeProduct = (id: string) => {
    const next = currentAppliesTo();
    next.productIds = next.productIds.filter((pid) => pid !== id);
    void saveAppliesTo(next, id === sale.primaryProductId ? { primaryProductId: null } : {});
  };
  const removeOffer = (id: string) => {
    const next = currentAppliesTo();
    next.offerIds = next.offerIds.filter((oid) => oid !== id);
    void saveAppliesTo(next, id === sale.primaryOfferId ? { primaryOfferId: null } : {});
  };
  const addProduct = (id: string) => {
    const next = currentAppliesTo();
    if (!next.productIds.includes(id)) next.productIds.push(id);
    void saveAppliesTo(next);
  };
  const addOffer = (id: string) => {
    const next = currentAppliesTo();
    if (!next.offerIds.includes(id)) next.offerIds.push(id);
    void saveAppliesTo(next);
  };

  return (
    <div className="w-full px-8 pb-10">
      <div className="sticky top-0 z-10 -mx-8 px-8 py-2 mb-4 bg-background/80 backdrop-blur border-b border-border/40 flex items-center justify-between gap-3">
        <Link href={`/brands/${brandId}/sales-discounts`}>
          <Button variant="ghost" size="sm" className="h-9 -ml-2 text-muted-foreground hover:text-foreground" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sales &amp; Discounts
          </Button>
        </Link>
        <div className="flex items-center gap-2">
          {saveMutation.isPending && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="indicator-saving">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
            </span>
          )}
          <Button variant="outline" size="sm" className="h-9" onClick={() => duplicateMutation.mutate()} disabled={duplicateMutation.isPending} data-testid="button-duplicate-sale">
            {duplicateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Copy className="h-4 w-4 mr-2" />}
            Duplicate
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/5" data-testid="button-delete-sale">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this sale?</AlertDialogTitle>
                <AlertDialogDescription>This permanently removes the sale. Ad runs that already snapshotted this sale will keep their copy unchanged.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="button-confirm-delete" onClick={() => deleteMutation.mutate()}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="space-y-4">
        {/* ── Hero ──────────────────────────────────────────────────── */}
        <SectionCard testId="sale-hero" className="p-5 md:p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="relative w-full lg:w-[380px] xl:w-[440px] flex-shrink-0 aspect-[4/3] rounded-2xl overflow-hidden border border-border/50 bg-muted/40">
              {displayHero ? (
                <img src={optimizedImg(displayHero, 1280)} alt={sale.name} className="absolute inset-0 w-full h-full object-cover" data-testid="img-sale-hero" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-rose-100/70 via-muted/60 to-amber-100/50 dark:from-rose-900/20 dark:via-muted/40 dark:to-amber-900/15 flex items-center justify-center" data-testid="placeholder-sale-hero">
                  <Percent className="h-14 w-14 text-muted-foreground/25" />
                </div>
              )}
              <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em]">
                  <Percent className="h-3 w-3 text-rose-500" /> Sale
                </span>
                {hasText(typeLabel) && (
                  <span className="rounded-full bg-background/85 backdrop-blur px-2.5 py-1 text-[10px] font-semibold capitalize" data-testid="badge-hero-type">
                    {typeLabel}
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0 space-y-4">
              <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                <InlineText
                  value={sale.name}
                  onSave={(v) => { if (v) return save({ name: v }); }}
                  placeholder="Sale name"
                  testId="sale-name"
                />
              </h1>

              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  ready
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                }`}
                data-testid="pill-sale-ready"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${ready ? "bg-emerald-500" : "bg-amber-500"}`} />
                {ready ? "Sale Ready" : "In Progress"}
              </span>

              <div className="flex items-center gap-3">
                <StrengthRing pct={strength} />
                <div>
                  <p className="text-sm font-semibold">Sale Strength</p>
                  <p className="text-xs text-muted-foreground" data-testid="text-strength-caption">{saleStrengthCaption(strength)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <EditableMetaChip label="Type" testId="chip-meta-type">
                  <InlineSelect value={sale.saleType} options={SALE_TYPES} onSave={(v) => save({ saleType: v })} testId="sale-type" />
                </EditableMetaChip>
                <EditableMetaChip label="Discount" testId="chip-meta-discount">
                  <InlineText
                    value={sale.discountValue}
                    onSave={(v) => save({ discountValue: v || null })}
                    placeholder="Add…"
                    testId="sale-discount-value"
                  />
                </EditableMetaChip>
                <EditableMetaChip label="Urgency" testId="chip-meta-urgency">
                  <InlineSelect
                    value={sale.urgencyMechanism ?? "no_urgency"}
                    options={URGENCY_MECHANISMS}
                    onSave={(v) => save({ urgencyMechanism: v })}
                    testId="sale-urgency"
                  />
                </EditableMetaChip>
                <EditableMetaChip label="Expiration" testId="chip-meta-expiration">
                  <InlineDate
                    value={sale.expirationDate ? new Date(sale.expirationDate as any).toISOString() : null}
                    display={expires}
                    onSave={(iso) => save({ expirationDate: iso })}
                    placeholder="Add date…"
                    testId="sale-expiration"
                  />
                </EditableMetaChip>
                <EditableMetaChip label="Status" testId="chip-meta-status">
                  <InlineSelect value={sale.status} options={SALE_STATUSES} onSave={(v) => save({ status: v })} testId="sale-status" />
                </EditableMetaChip>
              </div>

              <InlineTextarea
                value={sale.promotionSummary}
                onSave={(v) => save({ promotionSummary: v || null })}
                placeholder="Add promotion summary…"
                className="text-sm text-muted-foreground leading-relaxed"
                testId="sale-summary"
              />
            </div>
          </div>
        </SectionCard>

        {/* ── Sale psychology ───────────────────────────────────────── */}
        <SectionCard testId="section-psychology">
          <SectionHeader icon={Brain} title="Sale Psychology" accent="bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {psychCards.map((c) => (
              <div key={c.key} className={`rounded-xl border p-4 ${c.tint}`} data-testid={`card-psych-${c.key}`}>
                <div className={`flex items-center gap-1.5 mb-2 ${c.head}`}>
                  <c.icon className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em]">{c.label}</span>
                </div>
                {c.field ? (
                  <InlineTextarea
                    value={(sale as any)[c.field]}
                    onSave={(v) => save({ [c.field!]: v || null })}
                    placeholder={`Add ${c.label.toLowerCase()}…`}
                    className="text-sm leading-relaxed"
                    testId={`psych-${c.key}`}
                  />
                ) : (
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {hasText(c.text) ? c.text : <span className="italic text-muted-foreground/50">{c.derivedHint}</span>}
                  </p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Quote + promo code strip (echoes the sale card) ───────── */}
        <div className="rounded-2xl bg-amber-500/[0.05] border border-amber-500/10 px-5 py-5 flex items-center gap-4" data-testid="section-quote-strip">
          <div className="flex-[1.2] min-w-0 flex gap-2.5 self-center">
            <span className="text-[46px] leading-[36px] text-emerald-800/75 select-none flex-shrink-0" style={SERIF}>&ldquo;</span>
            <p
              className={`italic text-[15px] leading-[22px] self-center ${quote ? "text-foreground/85" : "text-muted-foreground/45"}`}
              style={SERIF}
              data-testid="text-sale-quote"
            >
              {quote || "No customer-facing copy captured yet — add it in the Customer-Facing Copy card above"}
            </p>
          </div>
          {sketch && (
            /* Same treatment as the card: full-strength multiply melts the
               cream PNG into the tan strip (invert+screen in dark mode),
               and a radial mask fades the artwork's edges. */
            <div className="relative hidden sm:block w-[150px] flex-shrink-0 self-stretch min-h-[100px]" data-testid="img-sale-sketch">
              <img
                src={optimizedImg(sketch, 480)}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute left-0 top-1/2 -translate-y-1/2 h-[110px] w-full object-contain mix-blend-multiply dark:invert dark:mix-blend-screen dark:opacity-70 pointer-events-none select-none"
                style={{
                  WebkitMaskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                  maskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                }}
              />
            </div>
          )}
          <div className="flex-1 min-w-0 border-l border-amber-500/10 pl-4">
            <p className="text-[10px] leading-[14px] font-bold uppercase tracking-[0.15em] text-amber-600/90 mb-1">Promo Code</p>
            <InlineText
              value={promoCode}
              onSave={(v) => save({ promoCode: v || null })}
              placeholder="Add promo code…"
              className="text-[13px] leading-[18px] font-mono font-semibold tracking-[0.03em] text-foreground/85 break-all"
              testId="sale-promo-code"
            />
          </div>
        </div>

        {/* ── Applies To ────────────────────────────────────────────── */}
        <SectionCard testId="section-applies-to">
          <SectionHeader
            icon={Target} title="Applies To"
            accent="bg-sky-100 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300"
            count={(productEntries.length + offerEntries.length) || undefined}
          />
          <div className="flex flex-wrap gap-2 mb-4">
            <EditableMetaChip label="Scope" testId="chip-applies-scope">
              <InlineSelect
                value={scope}
                options={APPLIES_TO_SCOPES}
                onSave={(v) => saveAppliesTo({ ...currentAppliesTo(), scope: v })}
                testId="applies-scope"
              />
            </EditableMetaChip>
          </div>
          {scope === "brand" && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-1" data-testid="text-applies-brand-wide">
              This promotion covers the entire brand — every product and offer is included.
            </p>
          )}
          {(scope === "products" || productEntries.length > 0) && (
            <div className="mb-4" data-testid="applies-to-products">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-2">Products</p>
              <div className="flex flex-wrap gap-2 items-center">
                {productEntries.map((e, i) => (
                  <LinkedEntityTile
                    key={e.id} name={e.name} photo={e.photo} kind="product" primary={e.primary}
                    onRemove={scope === "products" ? () => removeProduct(e.id) : undefined}
                    testId={`tile-applies-product-${i}`}
                  />
                ))}
                {scope === "products" && addableProducts.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-xl border border-dashed border-border px-3 py-2.5 text-xs font-medium text-muted-foreground/70 hover:text-foreground hover:border-primary/40 transition-colors"
                        data-testid="button-add-applies-product"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add product
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-72 overflow-y-auto">
                      {addableProducts.map((p) => (
                        <DropdownMenuItem key={p.id} onClick={() => addProduct(p.id)} data-testid={`option-add-product-${p.id}`}>
                          {p.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          )}
          {(scope === "offers" || offerEntries.length > 0) && (
            <div data-testid="applies-to-offers">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-2">Offers</p>
              <div className="flex flex-wrap gap-2 items-center">
                {offerEntries.map((e, i) => (
                  <LinkedEntityTile
                    key={e.id} name={e.name} photo={e.photo} kind="offer" primary={e.primary}
                    onRemove={scope === "offers" ? () => removeOffer(e.id) : undefined}
                    testId={`tile-applies-offer-${i}`}
                  />
                ))}
                {scope === "offers" && addableOffers.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-xl border border-dashed border-border px-3 py-2.5 text-xs font-medium text-muted-foreground/70 hover:text-foreground hover:border-primary/40 transition-colors"
                        data-testid="button-add-applies-offer"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add offer
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-72 overflow-y-auto">
                      {addableOffers.map((o) => (
                        <DropdownMenuItem key={o.id} onClick={() => addOffer(o.id)} data-testid={`option-add-offer-${o.id}`}>
                          {o.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          )}
        </SectionCard>

        {/* ── Terms & conditions ────────────────────────────────────── */}
        <SectionCard testId="section-terms">
          <SectionHeader icon={FileText} title="Terms & Conditions" accent="bg-slate-200 text-slate-600 dark:bg-slate-800/70 dark:text-slate-300" />
          <InlineTextarea
            value={sale.termsConditions}
            onSave={(v) => save({ termsConditions: v || null })}
            placeholder="Add terms & conditions…"
            className="text-sm leading-relaxed text-muted-foreground"
            testId="sale-terms"
          />
        </SectionCard>

        {/* ── Creative notes ────────────────────────────────────────── */}
        <SectionCard testId="section-creative-notes">
          <SectionHeader icon={Paintbrush} title="Creative Notes" accent="bg-fuchsia-100 text-fuchsia-600 dark:bg-fuchsia-900/40 dark:text-fuchsia-300" />
          <InlineTextarea
            value={sale.creativeNotes}
            onSave={(v) => save({ creativeNotes: v || null })}
            placeholder="Add creative notes…"
            className="text-sm leading-relaxed"
            testId="sale-creative-notes"
          />
        </SectionCard>
      </div>
    </div>
  );
}
